/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  FlatList,
  RefreshControl,
  TouchableOpacity,
  ScrollView,
  StyleSheet,
  ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { usePlanEntitlements } from '../../shell/providers/PlanEntitlementsProvider';
import { useModulePermission } from '../../shell/session/hooks/useModulePermission';
import { useScreenLoad } from '../../shared/ui/hooks-screen/useScreenLoad';
import { ShellContentFrame } from '../../shared/layout/ShellContentFrame';
import { ModuleEmptyState } from '../../shared/ui/ModuleEmptyState';
import { VIRTUALIZED_LIST_PROPS } from '../../shared/ui/virtualizedListProps';
import { fetchComplaintsByOrganization } from '../infra/complaintsRepo';
import {
  ALLEGED_PARTY_LABELS,
  canonicalComplaintReason,
  complaintReasonFilterOptions,
  complaintReasonLabel,
  COMPLAINT_STATUS_LABELS,
  COMPLAINT_STATUS_COLORS,
  formatDeadlineLabel,
  hoursUntilDeadline,
  type ComplaintReason,
  type ComplaintRow,
  type ComplaintStatus,
} from '../domain/complaintTypes';
import { formatArPhoneDisplay } from '@shared/phone';
import { ticketColors } from '../../shared/theme/ticketColors';
import { usePublicStoreFeaturesVisible } from '../../shell/feature-flags/usePublicStoreFeaturesVisible';
import { formatMoney as formatComplaintAmount } from '../../shared/format/public-api';
import { formatLocalDateTime } from '@shared/vueltoDateTime';
import { ModalNestedPicker } from '../../shared/modals/ModalNestedPicker';

type FilterTab = 'all' | ComplaintStatus;

const FILTER_TABS: { key: FilterTab; label: string }[] = [
  { key: 'all', label: 'Todos' },
  { key: 'open', label: 'Abiertos' },
  { key: 'pending_customer', label: 'Esperando al cliente' },
  { key: 'resolved', label: 'Resueltos' },
  { key: 'escalated', label: 'Escalados' },
];

type DateRange = 'all' | 'today' | '7d' | '30d';
const DATE_RANGE_TABS: { key: DateRange; label: string }[] = [
  { key: 'all', label: 'Todo' },
  { key: 'today', label: 'Hoy' },
  { key: '7d', label: '7d' },
  { key: '30d', label: '30d' },
];

// Motivos ya consolidados a nivel display (ej. 'Demora' cubre delay + delivery_delay).
const REASON_FILTERS: { key: 'all' | ComplaintReason; label: string }[] = [
  { key: 'all', label: 'Todos los motivos' },
  ...complaintReasonFilterOptions(),
];

const PAGE_SIZE = 50;

function dateRangeMs(range: DateRange): number {
  switch (range) {
    case 'today': return 24 * 60 * 60 * 1000;
    case '7d': return 7 * 24 * 60 * 60 * 1000;
    case '30d': return 30 * 24 * 60 * 60 * 1000;
    default: return Infinity;
  }
}

type OnlineComplaintsScreenProps = {
  /** When embedded in OrdersInbox, pass `OrdersInbox` so the web header refresh button works. */
  refreshRouteName?: string;
  /** Prefer `open` when the inbox knows there are open complaints. */
  initialFilter?: FilterTab;
};

export function OnlineComplaintsScreen({
  refreshRouteName = 'OnlineComplaints',
  initialFilter = 'all',
}: OnlineComplaintsScreenProps) {
  const navigation = useNavigation<NativeStackNavigationProp<{ ComplaintDetail: { complaintId: string } }>>();
  const { entitlements } = usePlanEntitlements();
  const orgId = entitlements?.organization_id;
  const canView = useModulePermission('BranchSettings', 'view');
  const publicStoreOn = usePublicStoreFeaturesVisible();
  const [complaints, setComplaints] = useState<ComplaintRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState<FilterTab>(initialFilter);
  const [dateRange, setDateRange] = useState<DateRange>('all');
  const [reasonFilter, setReasonFilter] = useState<'all' | ComplaintReason>('all');
  const [reasonPickerOpen, setReasonPickerOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Client-side filters applied on top of server-fetched data
  const filteredComplaints = complaints.filter((c) => {
    if (reasonFilter !== 'all' && canonicalComplaintReason(c.reason) !== reasonFilter) {
      return false;
    }
    if (dateRange !== 'all') {
      const created = new Date(c.created_at).getTime();
      if (Date.now() - created > dateRangeMs(dateRange)) return false;
    }
    return true;
  });

  // Descartar respuestas stale al cambiar de filtro rápido
  // (embebido en OrdersInbox monta con 'open' y el usuario puede tocar "Todos"
  // antes de que resuelva el primer fetch).
  const requestIdRef = useRef(0);

  const load = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    if (!orgId || !canView) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(false);
    try {
      const statusFilter = filter === 'all' ? undefined : [filter];
      const data = await fetchComplaintsByOrganization(orgId, statusFilter, 0);
      if (requestId !== requestIdRef.current) return;
      setComplaints(data);
      setPage(0);
      setHasMore(data.length >= PAGE_SIZE);
    } catch (e) {
      if (requestId !== requestIdRef.current) return;
      console.error('OnlineComplaintsScreen load', e);
      setError(true);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [orgId, canView, filter]);

  const { refreshing, onRefresh } = useScreenLoad(refreshRouteName, load);
  useEffect(() => {
    void load();
  }, [load]);

  const loadMore = useCallback(async () => {
    if (!orgId || loadingMore || !hasMore) return;
    const requestId = requestIdRef.current;
    setLoadingMore(true);
    try {
      const statusFilter = filter === 'all' ? undefined : [filter];
      const nextPage = page + 1;
      const data = await fetchComplaintsByOrganization(orgId, statusFilter, nextPage);
      if (requestId !== requestIdRef.current) return;
      setComplaints((prev) => [...prev, ...data]);
      setPage(nextPage);
      setHasMore(data.length >= PAGE_SIZE);
    } catch (e) {
      console.error('OnlineComplaintsScreen loadMore', e);
    } finally {
      setLoadingMore(false);
    }
  }, [orgId, filter, page, loadingMore, hasMore]);

  if (!canView) {
    return (
      <ShellContentFrame style={{ flex: 1 }}>
        <ModuleEmptyState
          title="No podés ver Reclamos"
          subtitle="Pedile al dueño que te dé permiso."
          testID="complaints.no-permission"
        />
      </ShellContentFrame>
    );
  }

  if (!publicStoreOn) {
    return (
      <ShellContentFrame style={{ flex: 1 }}>
        <ModuleEmptyState
          title="Tienda online desactivada"
          subtitle="Activá la tienda online para recibir reclamos."
          testID="complaints.no-store"
        />
      </ShellContentFrame>
    );
  }

  const reasonLabel =
    REASON_FILTERS.find((r) => r.key === reasonFilter)?.label ?? 'Todos';

  const renderItem = ({ item }: { item: ComplaintRow }) => {
    const hoursLeft = hoursUntilDeadline(item.deadline_at);
    const isUrgent = item.status === 'open' && hoursLeft < 24;
    const photoCount = item.photo_urls?.length ?? 0;
    const orderRef =
      item.subject_kind === 'appointment'
        ? item.appointment_label ?? `Turno · ${(item.appointment_id ?? item.id).slice(0, 8)}`
        : `Pedido ${item.display_code ? `#${item.display_code}` : `#${(item.order_id ?? item.id).slice(0, 8)}`}`;
    const phoneLabel = item.customer_phone ? formatArPhoneDisplay(item.customer_phone) : null;
    const metaBits = [
      // Si el nombre cayó al fallback del teléfono, no repetirlo en el meta.
      phoneLabel && phoneLabel !== item.customer_name ? phoneLabel : null,
      item.subject_kind !== 'appointment' && item.order_total > 0
        ? formatComplaintAmount(item.order_total)
        : null,
      photoCount > 0 ? `${photoCount} foto${photoCount === 1 ? '' : 's'}` : null,
    ].filter(Boolean);

    return (
      <TouchableOpacity
        style={styles.card}
        onPress={() => navigation.navigate('ComplaintDetail', { complaintId: item.id })}
        accessibilityRole="button"
        accessibilityLabel={`Reclamo de ${item.customer_name}, ${complaintReasonLabel(item.reason)}`}
        testID={`complaint-card-${item.id}`}
      >
        <View style={styles.cardHeader}>
          <Text style={styles.cardTitle} numberOfLines={1}>
            {item.customer_name}
          </Text>
          <View style={[styles.statusBadge, { backgroundColor: COMPLAINT_STATUS_COLORS[item.status] }]}>
            <Text style={styles.statusText}>{COMPLAINT_STATUS_LABELS[item.status]}</Text>
          </View>
        </View>
        <Text style={styles.reason} numberOfLines={1}>
          {complaintReasonLabel(item.reason)}
          {item.alleged_party && item.alleged_party !== 'merchant'
            ? ` · ${ALLEGED_PARTY_LABELS[item.alleged_party] ?? item.alleged_party}`
            : ''}
        </Text>
        <Text style={styles.meta} numberOfLines={1}>
          {orderRef}
          {metaBits.length > 0 ? ` · ${metaBits.join(' · ')}` : ''}
        </Text>
        <Text style={styles.date}>{formatLocalDateTime(item.created_at)}</Text>
        {item.status === 'open' ? (
          <Text style={[styles.deadline, isUrgent && styles.urgent]}>
            {formatDeadlineLabel(item.deadline_at)}
          </Text>
        ) : null}
      </TouchableOpacity>
    );
  };

  return (
    <ShellContentFrame style={styles.container}>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        style={styles.filterRow}
        contentContainerStyle={styles.filterRowContent}
        accessibilityRole="tablist"
        testID="complaints-filter-tabs"
      >
        {FILTER_TABS.map((tab) => {
          const selected = filter === tab.key;
          return (
            <TouchableOpacity
              key={tab.key}
              style={[styles.filterTab, selected && styles.filterTabActive]}
              onPress={() => setFilter(tab.key)}
              accessibilityRole="tab"
              accessibilityState={{ selected }}
              aria-selected={selected}
              accessibilityLabel={tab.label}
              testID={`complaints-filter-${tab.key}`}
            >
              <Text style={[styles.filterTabText, selected && styles.filterTabTextActive]}>
                {tab.label}
              </Text>
            </TouchableOpacity>
          );
        })}
      </ScrollView>

      {/* Secondary filters: date chips + motivo como dropdown (menos filas, claro affordance). */}
      <View style={styles.secondaryFilterRow}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.secondaryFilterScroll}
          contentContainerStyle={styles.secondaryFilterContent}
        >
          {DATE_RANGE_TABS.map((tab) => {
            const selected = dateRange === tab.key;
            return (
              <TouchableOpacity
                key={tab.key}
                style={[styles.chip, selected && styles.chipActive]}
                onPress={() => setDateRange(tab.key)}
                accessibilityRole="tab"
                accessibilityState={{ selected }}
                aria-selected={selected}
                accessibilityLabel={`Filtrar por fecha: ${tab.label}`}
                testID={`complaints-date-${tab.key}`}
              >
                <Text style={[styles.chipText, selected && styles.chipTextActive]}>
                  {tab.label}
                </Text>
              </TouchableOpacity>
            );
          })}
        </ScrollView>
        <TouchableOpacity
          style={[styles.chip, styles.reasonPickerBtn, reasonFilter !== 'all' && styles.chipActive]}
          onPress={() => setReasonPickerOpen(true)}
          accessibilityRole="button"
          accessibilityLabel={`Motivo: ${reasonLabel}`}
          testID="complaints-reason-picker"
        >
          <Text
            style={[styles.chipText, reasonFilter !== 'all' && styles.chipTextActive]}
            numberOfLines={1}
          >
            Motivo: {reasonLabel}
          </Text>
          <Ionicons
            name="chevron-down"
            size={12}
            color={reasonFilter !== 'all' ? '#FFFFFF' : ticketColors.ink2}
          />
        </TouchableOpacity>
      </View>

      <FlatList
        data={filteredComplaints}
        keyExtractor={(item) => item.id}
        renderItem={renderItem}
        refreshControl={
          <RefreshControl refreshing={loading || refreshing} onRefresh={onRefresh} />
        }
        onEndReached={() => void loadMore()}
        onEndReachedThreshold={0.3}
        ListFooterComponent={
          loadingMore ? (
            <View style={styles.footerLoading}>
              <ActivityIndicator color={ticketColors.gold} />
            </View>
          ) : null
        }
        contentContainerStyle={filteredComplaints.length === 0 ? styles.emptyContainer : undefined}
        ListEmptyComponent={
          error ? (
            <ModuleEmptyState
              title="No se pudieron cargar los reclamos"
              subtitle="Revisá tu conexión y probá de nuevo."
              primaryAction={{ label: 'Reintentar', onPress: () => void load(), testID: 'complaints-retry' }}
              testID="complaints.error"
            />
          ) : !loading ? (
            <ModuleEmptyState
              title="No hay reclamos"
              subtitle={
                complaints.length > 0 && filteredComplaints.length === 0
                  ? 'Probá con otros filtros.'
                  : 'Cuando un cliente haga un reclamo, aparece acá.'
              }
              testID="complaints.empty"
            />
          ) : null
        }
        {...VIRTUALIZED_LIST_PROPS}
      />

      <ModalNestedPicker
        visible={reasonPickerOpen}
        title="Motivo del reclamo"
        options={REASON_FILTERS.map((r) => ({ value: r.key, label: r.label }))}
        selectedValue={reasonFilter}
        onSelect={(value) => setReasonFilter((value || 'all') as 'all' | ComplaintReason)}
        onClose={() => setReasonPickerOpen(false)}
      />
    </ShellContentFrame>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: ticketColors.paper },
  filterRow: { flexGrow: 0 },
  filterRowContent: {
    flexDirection: 'row',
    paddingHorizontal: 12,
    paddingVertical: 8,
    gap: 6,
  },
  filterTab: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
    backgroundColor: ticketColors.paper2,
  },
  filterTabActive: { backgroundColor: ticketColors.gold },
  filterTabText: { fontSize: 12, fontWeight: '600', color: ticketColors.ink },
  filterTabTextActive: { color: ticketColors.goldInk },
  secondaryFilterRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingBottom: 6,
  },
  secondaryFilterScroll: { flexGrow: 0, flexShrink: 1 },
  secondaryFilterContent: {
    flexDirection: 'row',
    gap: 6,
    paddingVertical: 2,
  },
  reasonPickerBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    flexShrink: 0,
    maxWidth: 220,
  },
  chip: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 16,
    backgroundColor: ticketColors.paper2,
  },
  chipActive: { backgroundColor: ticketColors.ink },
  chipText: { fontSize: 11, fontWeight: '500', color: ticketColors.ink2 },
  chipTextActive: { color: '#fff' },
  card: {
    backgroundColor: ticketColors.card,
    borderRadius: 10,
    padding: 16,
    marginHorizontal: 12,
    marginVertical: 4,
    borderWidth: 1,
    borderColor: ticketColors.border,
  },
  cardHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: 8 },
  cardTitle: { flex: 1, fontSize: 16, fontWeight: '700', color: ticketColors.ink },
  statusBadge: { paddingHorizontal: 8, paddingVertical: 2, borderRadius: 10 },
  statusText: { fontSize: 11, fontWeight: '700', color: '#fff' },
  reason: { fontSize: 14, fontWeight: '600', color: ticketColors.ink, marginTop: 8 },
  meta: { fontSize: 13, color: ticketColors.inkLight, marginTop: 4 },
  date: { fontSize: 11, color: ticketColors.inkLight, marginTop: 2 },
  deadline: { fontSize: 12, fontWeight: '600', color: ticketColors.gold, marginTop: 6 },
  urgent: { color: ticketColors.bad, fontWeight: '700' },
  emptyContainer: { flex: 1, justifyContent: 'center' },
  footerLoading: { paddingVertical: 16, alignItems: 'center' },
});
